import { StationeryTemplateCategoryEnum } from '@graphql/generated-base';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useStationeryBasicDigitalV2ThemeIdsQuery } from './ProductDetailsPage.graphql.generated';

// The free Basic Digital V2 option only exists for the categories that have a
// digital experience today.
const SUPPORTED_CATEGORIES: ReadonlyArray<StationeryTemplateCategoryEnum> = [StationeryTemplateCategoryEnum.saveTheDate, StationeryTemplateCategoryEnum.invitation];

type UseIsBasicDigitalV2EnabledArgs = Readonly<{
  themeId: string;
  category: StationeryTemplateCategoryEnum;
}>;

type UseIsBasicDigitalV2EnabledResult = Readonly<{
  isEnabled: boolean;
  /**
   * True while the allowlist is still in flight. Callers must gate their product
   * list on this: the PDP seeds its selected-product state once, so resolving the
   * allowlist after that first render would drop a `?format=basicDigitalV2` deep
   * link onto the paper tab.
   */
  loading: boolean;
}>;

/**
 * Basic Digital V2 is gated on both the `printEnableBasicDigitalV2` Amplitude flag
 * and a themeId allowlist hardcoded in card_service, so a theme can be enabled
 * without a client release.
 */
export const useIsBasicDigitalV2Enabled = (args: UseIsBasicDigitalV2EnabledArgs): UseIsBasicDigitalV2EnabledResult => {
  const { themeId, category } = args;
  const isSupportedCategory = SUPPORTED_CATEGORIES.includes(category);

  // Skipped outside the supported categories so Amplitude does not record an
  // exposure for a user who could never have been shown the option.
  const [flagEnabled, flagLoading] = useIsFeatureEnabled('printEnableBasicDigitalV2', { skip: !isSupportedCategory });

  const shouldQuery = isSupportedCategory && flagEnabled;

  // The list is global and tiny, so the default cache-first policy serves every
  // other PDP in the session from cache.
  const { data, loading: themeIdsLoading } = useStationeryBasicDigitalV2ThemeIdsQuery({
    batchMode: 'fast',
    skip: !shouldQuery,
    ssr: true,
  });

  return {
    isEnabled: !!data?.stationeryBasicDigitalV2ThemeIds.includes(themeId),
    loading: isSupportedCategory && (flagLoading || (shouldQuery && themeIdsLoading)),
  };
};
